// Role + lightweight dashboard data for the shell.
//
// Design/021 rework: the old derived "leader" role (admin of any group) is
// retired as a *role*. Capabilities are now explicit boolean flags on the
// Person row (`isAdmin`, `publisher`, `eventLeader`). The `ledGroups`
// helper is kept because the Publisher-tools pages still need to know which
// groups the caller admins.
//
// Effective capabilities (design/032 split):
//   canPublish       = publisher || isAdmin          (courses, practices, content library, Leaders tab)
//   canManageGroups  = groupLeader || canPublish     (groups)
//   canCreateEvents  = eventLeader || canPublish     (events)
//   canSeeAdmin      = isAdmin
//
// In dev (import.meta.env.DEV) a preview override lets you view the UI as
// any role without separate accounts. It only affects what the UI shows/hides
// — gw still enforces real permissions on every request.
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';

const RoleContext = createContext(null);

export function RoleProvider({ children }) {
  const { user, me, isSuperadmin } = useAuth();
  const [groups, setGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [devOverride, setDevOverride] = useState(null); // 'member' | 'publisher' | 'event-leader' | 'super' | null

  useEffect(() => {
    let cancelled = false;
    if (!user) { setGroups([]); setEvents([]); return; }
    (async () => {
      const [gs, es] = await Promise.all([
        api.listGroups().catch(() => []),
        api.listEvents().catch(() => []),
      ]);
      if (cancelled) return;
      setGroups(Array.isArray(gs) ? gs : []);
      setEvents(Array.isArray(es) ? es : []);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const ledGroups = useMemo(
    () => groups.filter((g) => g?.admin && me?.id && g.admin.id === me.id),
    [groups, me]
  );

  const isPublisher = !!me?.publisher;
  const isEventLeader = !!me?.eventLeader;
  const isGroupLeader = !!me?.groupLeader;

  const canSeeAdmin = isSuperadmin;
  const canPublish = isPublisher || canSeeAdmin;
  const canManageGroups = isGroupLeader || canPublish;
  const canCreateEvents = isEventLeader || canPublish;
  const canSeeContentLibrary = canPublish;

  const realRole = canSeeAdmin ? 'super' : (isPublisher ? 'publisher' : 'member');
  const effectiveRole = (import.meta.env.DEV && devOverride) || realRole;

  // For the header pill (super > publisher > leader roles > member).
  const pillRole = canSeeAdmin ? 'super'
                  : isPublisher ? 'publisher'
                  : (isEventLeader && isGroupLeader) ? 'leader'
                  : isGroupLeader ? 'group-leader'
                  : isEventLeader ? 'event-leader'
                  : effectiveRole;

  const value = useMemo(() => ({
    realRole,
    effectiveRole,
    pillRole,
    devOverride,
    setDevOverride,
    groups,
    ledGroups,
    events,
    groupsCount: groups.length,
    eventsCount: events.length,
    canSeeAdmin,
    isPublisher,
    isEventLeader,
    isGroupLeader,
    canPublish,
    canManageGroups,
    canCreateEvents,
    canSeeContentLibrary,
    /** Kept for backwards compatibility with the old sidebar section name.
     *  "Leader tools" retired — the section is Publisher-gated now. */
    canSeeLeaderTools: canManageGroups,
    reloadGroups: () => api.listGroups().then((g) => setGroups(Array.isArray(g) ? g : [])).catch(() => {}),
  }), [realRole, effectiveRole, pillRole, devOverride, groups, ledGroups, events,
      canSeeAdmin, isPublisher, isEventLeader, isGroupLeader, canPublish, canManageGroups, canCreateEvents, canSeeContentLibrary]);

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used inside <RoleProvider>');
  return ctx;
}
